import Avatar from '-/Custom/Avatar';
import { DateTime } from 'luxon';
import Badge from '@/components/UI/Badge';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import getOffsetColor from '@/utils/getOffsetColor';

export const BadgeAssigneeListItemFragment = graphql(
  /* GraphQL */ `
    fragment BadgeAssigneeListItem on badge_assignee {
      id
      badge_id
      person_id
      event_id
      team_id
      updated_at
      person {
        id
        title: name
        avatar_id
        color
      }
      team {
        id
        title
        color: home_primary_color
      }
      badge {
        id
        color
      }
      badge_assignee_attributes_aggregate {
        aggregate {
          count
        }
      }
      preview_value
      badge_assignee_attributes(
        limit: 3
        order_by: { badge_attribute: { order: asc } }
      ) {
        id
        value
        badge_attribute {
          id
          title
        }
      }
    }
  `,
);

export default function BadgeAssigneeListItem({
  fragment,
}: {
  fragment: FragmentOf<typeof BadgeAssigneeListItemFragment>;
}) {
  const badgeAssignee = readFragment(BadgeAssigneeListItemFragment, fragment);
  const recipient = badgeAssignee.person;

  return (
    <Box direction="horizontal" flex space="md">
      <Box direction="vertical" space="xs">
        <Box direction="horizontal" space="md" align="center">
          <Avatar
            id={recipient?.avatar_id || ''}
            size="md"
            color={recipient?.color}
            name={recipient?.title || ''}
          />
          <Box direction="vertical">
            <Box direction="horizontal" space="sm">
              <Text numberOfLines={1} size="lg">
                {recipient?.title}
              </Text>
              {badgeAssignee.preview_value ? (
                <Badge color={badgeAssignee.badge.color}>
                  <Badge.Text>{badgeAssignee.preview_value}</Badge.Text>
                </Badge>
              ) : null}
            </Box>

            <Text fontWeight="normal" numberOfLines={1} size="xs">
              {DateTime.fromISO(badgeAssignee.updated_at).toLocaleString(
                DateTime.DATETIME_SHORT,
              )}
            </Text>
          </Box>
        </Box>
        <Box direction="horizontal" paddingLeft={19} space="sm">
          <Box
            backgroundColor={getOffsetColor(recipient?.color || '#000', false)}
            width={2}
            h="full"
          />
          <Box direction="vertical" pl="2xl">
            {badgeAssignee.badge_assignee_attributes.map(
              (badgeAssigneeAttribute) => {
                if (!badgeAssigneeAttribute.value) {
                  return null;
                }

                return (
                  <Text
                    variant="typographySecondary"
                    size="xs"
                    key={badgeAssigneeAttribute.badge_attribute.id}
                  >
                    {badgeAssigneeAttribute.badge_attribute.title}:{' '}
                    {badgeAssigneeAttribute.value}
                  </Text>
                );
              },
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
